import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Images, Loader2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

export default function MobilePhotoUploadPage() {
  const { token } = useParams();
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [photoCount, setPhotoCount] = useState(0);
  const [remaining, setRemaining] = useState(25);
  const [uploading, setUploading] = useState(false);

  const loadSession = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/photo-upload/${token}`);
      setStatus('open');
      setPhotoCount(data.photo_count || 0);
      setRemaining(data.remaining ?? 25);
    } catch (error) {
      const code = error.response?.status;
      setStatus(code === 410 ? 'expired' : 'invalid');
      setMessage(
        error.response?.data?.detail
        || 'This upload link is not valid. Ask the desk to generate a new QR code.'
      );
    }
  }, [token]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || uploading || status !== 'open') return;
    const formData = new FormData();
    files.forEach((file) => {
      const name = file.name || `photo-${Date.now()}.jpg`;
      formData.append('files', file, name);
    });
    setUploading(true);
    try {
      // Do not set Content-Type: the browser must add the multipart boundary.
      // A bare "multipart/form-data" header makes FastAPI return 422 on iOS.
      const { data } = await axios.post(
        `${BACKEND_URL}/api/photo-upload/${token}/images`,
        formData
      );
      setPhotoCount(data.photo_count || 0);
      setRemaining(data.remaining ?? 0);
      setMessage(
        (data.uploads || []).length === 1
          ? 'Photo sent to the inventory screen'
          : `${(data.uploads || []).length} photos sent to the inventory screen`
      );
    } catch (error) {
      const code = error.response?.status;
      if (code === 410) {
        setStatus('expired');
        setMessage('This upload link has expired. Generate a new QR code on the desktop.');
      } else {
        const detail = error.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : 'Upload failed. Try again.');
      }
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (libraryRef.current) libraryRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white px-5 py-8">
      <div className="max-w-md mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-['Manrope']">Drive Now Autohaus</p>
        <h1 className="text-2xl font-['Unbounded'] font-bold mt-2">Vehicle photos</h1>
        <p className="text-sm font-['Manrope'] text-white/55 mt-2">
          Take a picture or choose from Gallery / Files. Photos appear on the inventory computer immediately.
        </p>

        {status === 'loading' && (
          <div className="mt-10 flex items-center gap-2 text-white/60 font-['Manrope']">
            <Loader2 className="w-5 h-5 animate-spin" />
            Checking upload link…
          </div>
        )}

        {(status === 'expired' || status === 'invalid') && (
          <div className="mt-8 border border-red-500/30 bg-red-500/10 p-4 text-sm font-['Manrope'] text-red-200">
            {message}
          </div>
        )}

        {status === 'open' && (
          <div className="mt-8 space-y-3">
            <button
              type="button"
              disabled={uploading || remaining <= 0}
              onClick={() => cameraRef.current?.click()}
              className="w-full min-h-[3.25rem] px-4 py-3 bg-blue-500 text-white font-['Manrope'] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              Take photo
            </button>
            <button
              type="button"
              disabled={uploading || remaining <= 0}
              onClick={() => libraryRef.current?.click()}
              className="w-full min-h-[3.25rem] px-4 py-3 border border-white/20 text-white font-['Manrope'] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Images className="w-5 h-5" />
              Gallery / Files
            </button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*,.heic,.heif"
              capture="environment"
              className="hidden"
              onChange={(e) => uploadFiles(e.target.files)}
            />
            <input
              ref={libraryRef}
              type="file"
              accept={`${ACCEPT},.heic,.heif,image/*`}
              multiple
              className="hidden"
              onChange={(e) => uploadFiles(e.target.files)}
            />
            <p className="text-sm font-['Manrope'] text-white/50 pt-2">
              {uploading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading…
                </span>
              ) : (
                <>
                  <Upload className="w-4 h-4 inline mr-1" />
                  {photoCount} uploaded · {remaining} remaining
                </>
              )}
            </p>
            {message && (
              <p className="text-sm font-['Manrope'] text-green-300">{message}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
